// The accent color follows the field. When a painting wave's front reaches an element that uses
// the accent, the element picks up the wave's palette, just like the field's cells do
// (field/palettes.ts). The elements:
// - the links (their hovers and the end mark)
// - the bio (its phrases' hover)
// - home's role line (its map pin)
// - the corner plates (the settings gear and its switches, and the about mark)
// - the shelf labels (their marks)
// - the lists (their bullets)
// - the video players (their play buttons and played cells)
//
// Each one carries its palette's accent as an inline custom property, a `light-dark()` of the
// two themes' accents, the same as the theme's own token (_theme.scss), so a theme change
// doesn't need a repaint. None of them contains another, so each changes on its own.
//
// The tab's favicon switches to the newest painting wave's palette as soon as that wave starts.
// Waiting for the wave to end would leave it seconds behind the page.

import { themePalette } from "../field/palettes.js";
import { paletteAt, type Painting } from "../waves/palette-waves.js";
import { currentTheme, onThemeChange } from "../page/theme.js";
import { publishFavicon } from "../page/favicon.js";
import { pageScrollX, pageScrollY } from "../page/page-scroll.js";
import type { WaveRing } from "../waves/waves.js";
import { gustReachesBox, type Box } from "@bfly/engine";
import { boxReached, channelOf, type WaveAges } from "../waves/wave-ages.js";

const TARGETS = "a, #bio, .role, jrsm-video, .shelf-label, .list";

// A target and its box, in page pixels. For a target fixed to the viewport (the corner plates),
// which scrolling doesn't move, it's in viewport pixels.
interface Target {
  element: HTMLElement;
  fixed: boolean;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface Accents {
  /** Every frame: the live waves' rings, the palettes they paint, and the field's gust ages
      (wave-ages.ts). Ages are null without the field, and then the fallback front decides. */
  setWaves(rings: readonly WaveRing[], painting: Painting, ages: WaveAges | null): void;
}

/** Repaints the accents of the elements in `root`, and of `fixed`, the elements fixed to the
    viewport. */
export function startAccents(root: HTMLElement, fixed: readonly HTMLElement[] = []): Accents {
  // The palette each element shows, once a wave has painted it. The rest show the first
  // palette, the theme's own.
  const shown = new Map<HTMLElement, number>();
  let lastBase = 0;
  let faviconPalette = 0;
  // The targets and their boxes are kept from frame to frame. A wave's frame gets here after
  // the bio may have written to the page, so measuring every frame would force a layout. When
  // the layout changes, the next painting frame measures again: that's a new size for the root
  // (the web font loads, a rewrite adds a line) or for the window (the column moves).
  let targets: Target[] | null = null;
  const stale = (): void => {
    targets = null;
  };
  new ResizeObserver(stale).observe(root);
  window.addEventListener("resize", stale, { passive: true });
  // The field's ages for this frame, for `reachedByAges`.
  let frameAges: WaveAges | null = null;
  const reachedByAges = (ring: WaveRing, box: Box): boolean =>
    frameAges !== null && boxReached(frameAges, channelOf(frameAges, ring.id), box);
  // One target's box in viewport pixels (the rings' space), reused so a frame makes no new
  // objects.
  const view = { left: 0, top: 0, right: 0, bottom: 0 };

  function measure(): Target[] {
    const scrollX = pageScrollX();
    const scrollY = pageScrollY();
    const inPage = [...root.querySelectorAll(TARGETS)].flatMap((element) =>
      element instanceof HTMLElement ? [target(element, false, scrollX, scrollY)] : [],
    );
    return [...inPage, ...fixed.map((element) => target(element, true, 0, 0))];
  }

  function target(element: HTMLElement, fixed: boolean, scrollX: number, scrollY: number): Target {
    const box = element.getBoundingClientRect();
    return {
      element,
      fixed,
      left: box.left + scrollX,
      top: box.top + scrollY,
      right: box.right + scrollX,
      bottom: box.bottom + scrollY,
    };
  }

  function paint(element: HTMLElement, palette: number): void {
    const light = themePalette(palette, "light").accent;
    const dark = themePalette(palette, "dark").accent;
    element.style.setProperty("--accent", `light-dark(${light}, ${dark})`);
  }

  // The favicon is its own image, so it has to pick the theme's side of the accent itself.
  function paintFavicon(): void {
    publishFavicon(document, themePalette(faviconPalette, currentTheme()).accent);
  }

  onThemeChange(paintFavicon);
  paintFavicon();

  return {
    setWaves(rings, painting, ages) {
      frameAges = ages;
      // The waves are oldest first, so the last one that paints is the newest.
      let newest = painting.base;
      for (const palette of painting.waves) if (palette >= 0) newest = palette;
      if (newest !== faviconPalette) {
        faviconPalette = newest;
        paintFavicon();
      }
      // Only a painting wave (or the end of one) changes an accent.
      if (painting.waves.every((palette) => palette < 0) && painting.base === lastBase) return;
      lastBase = painting.base;
      targets ??= measure();
      const scrollX = pageScrollX();
      const scrollY = pageScrollY();
      for (const { element, fixed, left, top, right, bottom } of targets) {
        const x = fixed ? 0 : scrollX;
        const y = fixed ? 0 : scrollY;
        view.left = left - x;
        view.top = top - y;
        view.right = right - x;
        view.bottom = bottom - y;
        const palette = paletteAt(view, rings, painting, ages ? reachedByAges : gustReachesBox);
        if (palette === (shown.get(element) ?? 0)) continue;
        shown.set(element, palette);
        paint(element, palette);
      }
    },
  };
}
